import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/Button';
import { TimeSelect } from '../../components/TimeSelect';
import { useToast } from '../../hooks/useToast';
import {
  ApiError,
  createMaintenanceWindow,
  deleteMaintenanceWindow,
  getOpeningHours,
  listMaintenanceWindows,
  putOpeningHours,
  type AffectedBooking,
  type MaintenanceWindow,
  type OpeningHoursDay,
} from '../../lib/api';
import { formatClock, minutesOfDay, todayLocal } from '../../lib/time';
import { PS, T } from '../../lib/theme';
import { orderByDateTimeDescending } from '../../lib/admin-list-order';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function closesNextDay(day: OpeningHoursDay): boolean {
  const open = minutesOfDay(day.open_time);
  const close = minutesOfDay(day.close_time);
  return !day.is_closed && day.close_time !== '24:00' && open !== null && close !== null && close <= open;
}

function endsNextDay(startTime: string, endTime: string): boolean {
  const start = minutesOfDay(startTime);
  const end = minutesOfDay(endTime);
  return start !== null && end !== null && end <= start;
}

function followingDate(date: string): string {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

function sortWindows(windows: MaintenanceWindow[]): MaintenanceWindow[] {
  return orderByDateTimeDescending(windows, (window) => ({ date: window.starts_at, id: window.id }));
}

const REASON_TEXT: Record<AffectedBooking['reason'], string> = {
  outside_opening_hours: 'Outside club opening hours',
  maintenance_window: 'Inside a maintenance window',
};

/**
 * Shared save/create report. A booking link keeps the date filter that the
 * bookings page reads, so staff land directly on the conflict they need to act on.
 */
function AffectedBookingsPanel({ bookings }: { bookings: AffectedBooking[] }) {
  const orderedBookings = orderByDateTimeDescending(
    bookings,
    (booking) => ({ date: booking.date, time: booking.start_time, id: booking.id }),
  );
  return (
    <section aria-label="Affected bookings" style={panelStyle}>
      <h2 style={{ ...T.subheading, margin: 0 }}>Affected bookings</h2>
      {bookings.length === 0 ? (
        <p style={{ ...T.ui, color: PS.textSec, margin: 0 }}>No bookings affected</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Table</th>
                <th style={thStyle}>Owner</th>
                <th style={thStyle}>Payment</th>
                <th style={thStyle}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {orderedBookings.map((booking) => (
                <tr key={booking.id}>
                  <td style={tdStyle}>
                    <Link
                      to={`/bookings?date=${booking.date}`}
                      aria-label={`View bookings for ${booking.date}`}
                      style={{ color: PS.linkBlue }}
                    >
                      {booking.date}
                    </Link>
                  </td>
                  <td style={tdStyle}>{formatClock(booking.start_time)}–{formatClock(booking.end_time)}</td>
                  <td style={tdStyle}>{booking.table_name}</td>
                  <td style={tdStyle}>{booking.owner_name ?? '—'}</td>
                  <td style={tdStyle}>{booking.payment_type}</td>
                  <td style={tdStyle}>{REASON_TEXT[booking.reason]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function ClubHoursPage() {
  const { push } = useToast();
  const [days, setDays] = useState<OpeningHoursDay[]>([]);
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [affectedBookings, setAffectedBookings] = useState<AffectedBooking[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [maintenanceDate, setMaintenanceDate] = useState(todayLocal());
  const [maintenanceStart, setMaintenanceStart] = useState('09:00');
  const [maintenanceEnd, setMaintenanceEnd] = useState('10:00');
  const [maintenanceReason, setMaintenanceReason] = useState('');
  const [creatingWindow, setCreatingWindow] = useState(false);
  const [deletingWindowId, setDeletingWindowId] = useState<number | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setLoadError(null);
    try {
      const [schedule, windows] = await Promise.all([
        getOpeningHours(signal),
        listMaintenanceWindows(signal),
      ]);
      if (signal?.aborted) return;
      setDays([...schedule.days].sort((a, b) => a.day_of_week - b.day_of_week));
      setWindows(sortWindows(windows));
    } catch (err) {
      if (signal?.aborted) return;
      const message = err instanceof ApiError ? err.message : 'Could not load club hours.';
      setLoadError(message);
      push('error', message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [push]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const updateDay = (dayOfWeek: number, patch: Partial<OpeningHoursDay>) => {
    setDays((current) => current.map((day) => (
      day.day_of_week === dayOfWeek ? { ...day, ...patch } : day
    )));
  };

  const applyMondayToAll = () => {
    const monday = days.find((day) => day.day_of_week === 0);
    if (!monday) return;
    setDays(days.map((day) => ({ ...monday, day_of_week: day.day_of_week })));
  };

  const save = async () => {
    setSaving(true);
    try {
      // `closes_next_day` is an API-derived display field, not an input. Keep
      // this component's mutation intent unambiguous even before the wrapper
      // performs its own defensive body narrowing.
      const saved = await putOpeningHours(days.map(({ day_of_week, is_closed, open_time, close_time }) => ({
        day_of_week,
        is_closed,
        open_time,
        close_time,
      })));
      setDays([...saved.days].sort((a, b) => a.day_of_week - b.day_of_week));
      setAffectedBookings(saved.affected_bookings ?? []);
      push('success', 'Opening hours saved.');
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Could not save opening hours.');
    } finally {
      setSaving(false);
    }
  };

  const addMaintenanceWindow = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!maintenanceDate) return;
    // `24:00` is a UI-only end-of-day sentinel; ISO local datetimes express
    // that boundary as midnight on the following calendar day.
    const endIsEndOfDay = maintenanceEnd === '24:00';
    const nextDay = endIsEndOfDay || endsNextDay(maintenanceStart, maintenanceEnd);
    setCreatingWindow(true);
    try {
      const result = await createMaintenanceWindow({
        starts_at: `${maintenanceDate}T${maintenanceStart}`,
        ends_at: `${nextDay ? followingDate(maintenanceDate) : maintenanceDate}T${endIsEndOfDay ? '00:00' : maintenanceEnd}`,
        reason: maintenanceReason.trim() || null,
      });
      setWindows((current) => sortWindows([...current, result.window]));
      setAffectedBookings(result.affected_bookings);
      setMaintenanceReason('');
      push('success', 'Maintenance window added.');
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Could not add maintenance window.');
    } finally {
      setCreatingWindow(false);
    }
  };

  const removeMaintenanceWindow = async (maintenanceWindow: MaintenanceWindow) => {
    if (!window.confirm('Delete this maintenance window?')) return;
    setDeletingWindowId(maintenanceWindow.id);
    try {
      await deleteMaintenanceWindow(maintenanceWindow.id);
      setWindows((current) => current.filter((entry) => entry.id !== maintenanceWindow.id));
      // The last save/create report described a configuration that no longer
      // exists; keeping it would show bookings as blocked by a removed window.
      setAffectedBookings(null);
      push('success', 'Maintenance window deleted.');
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Could not delete maintenance window.');
    } finally {
      setDeletingWindowId(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1120 }}>
      <header>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Club hours</h1>
        <p style={{ ...T.ui, color: PS.textSec, margin: '6px 0 0' }}>
          Set the weekly schedule. Times are in Asia/Tbilisi.
        </p>
      </header>

      {loading && <div style={{ ...T.caption }}>Loading…</div>}
      {loadError && (
        <div style={errorStyle}>
          {loadError} <button onClick={() => void load()} style={retryStyle}>Retry</button>
        </div>
      )}

      {!loading && !loadError && (
        <section style={panelStyle} aria-labelledby="weekly-schedule-heading">
          <h2 id="weekly-schedule-heading" style={{ ...T.subheading, margin: 0 }}>Weekly schedule</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {days.map((day) => {
              const name = WEEKDAYS[day.day_of_week] ?? `Day ${day.day_of_week}`;
              return (
                <div key={day.day_of_week} style={dayRowStyle}>
                  <strong style={{ ...T.ui, minWidth: 90 }}>{name}</strong>
                  <label style={closedLabelStyle}>
                    <input
                      type="checkbox"
                      checked={day.is_closed}
                      onChange={(event) => updateDay(day.day_of_week, { is_closed: event.target.checked })}
                      aria-label={`${name} closed`}
                      disabled={saving}
                    />
                    Closed
                  </label>
                  <TimeSelect
                    value={day.open_time}
                    onChange={(open_time) => updateDay(day.day_of_week, { open_time })}
                    min="00:00"
                    max="24:00"
                    ariaLabel={`${name} opening time`}
                    disabled={day.is_closed || saving}
                  />
                  <span style={{ ...T.caption, color: PS.textSec }}>to</span>
                  <TimeSelect
                    value={day.close_time}
                    onChange={(close_time) => updateDay(day.day_of_week, { close_time })}
                    min="00:00"
                    max="24:00"
                    includeMax
                    ariaLabel={`${name} closing time`}
                    disabled={day.is_closed || saving}
                  />
                  {closesNextDay(day) && (
                    <span style={{ ...T.caption, color: PS.tealDeep }}>Closes next day</span>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button type="button" variant="secondary" onClick={applyMondayToAll} disabled={days.length !== 7 || saving}>
              Apply to all days
            </Button>
            <Button type="button" onClick={() => void save()} disabled={saving || days.length !== 7}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </section>
      )}

      {!loading && !loadError && (
        <section style={panelStyle} aria-labelledby="maintenance-windows-heading">
          <h2 id="maintenance-windows-heading" style={{ ...T.subheading, margin: 0 }}>Maintenance windows</h2>
          <form onSubmit={(event) => void addMaintenanceWindow(event)} style={maintenanceFormStyle}>
            <div style={fieldStyle}>
              <label htmlFor="maintenance-date" style={T.label}>Date</label>
              <input
                id="maintenance-date"
                type="date"
                value={maintenanceDate}
                onChange={(event) => setMaintenanceDate(event.target.value)}
                aria-label="Maintenance date"
                required
                disabled={creatingWindow}
                style={inputStyle}
              />
            </div>
            <div style={fieldStyle}>
              <span style={T.label}>Start</span>
              <TimeSelect
                value={maintenanceStart}
                onChange={setMaintenanceStart}
                min="00:00"
                max="24:00"
                ariaLabel="Maintenance start time"
                disabled={creatingWindow}
              />
            </div>
            <div style={fieldStyle}>
              <span style={T.label}>End</span>
              <TimeSelect
                value={maintenanceEnd}
                onChange={setMaintenanceEnd}
                min="00:00"
                max="24:00"
                includeMax
                ariaLabel="Maintenance end time"
                disabled={creatingWindow}
              />
              {endsNextDay(maintenanceStart, maintenanceEnd) && (
                <span style={{ ...T.caption, color: PS.tealDeep }}>Ends next day</span>
              )}
            </div>
            <div style={{ ...fieldStyle, flex: '1 1 240px' }}>
              <label htmlFor="maintenance-reason" style={T.label}>Reason (optional)</label>
              <input
                id="maintenance-reason"
                value={maintenanceReason}
                onChange={(event) => setMaintenanceReason(event.target.value)}
                aria-label="Maintenance reason"
                maxLength={200}
                disabled={creatingWindow}
                style={inputStyle}
              />
            </div>
            <Button type="submit" disabled={creatingWindow || !maintenanceDate} style={{ alignSelf: 'end' }}>
              {creatingWindow ? 'Adding…' : 'Add maintenance window'}
            </Button>
          </form>

          {windows.length === 0 ? (
            <p style={{ ...T.ui, color: PS.textSec, margin: 0 }}>No upcoming maintenance windows</p>
          ) : (
            <ul aria-label="Scheduled maintenance windows" style={windowsListStyle}>
              {windows.map((maintenanceWindow) => (
                <li key={maintenanceWindow.id} style={windowRowStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={T.ui}>
                      {maintenanceWindow.starts_at.slice(0, 10)} {formatClock(maintenanceWindow.starts_at.slice(11, 16))}
                      {' – '}
                      {maintenanceWindow.ends_at.slice(0, 10)} {formatClock(maintenanceWindow.ends_at.slice(11, 16))}
                    </span>
                    {maintenanceWindow.reason && <span style={{ ...T.caption, color: PS.textSec }}>{maintenanceWindow.reason}</span>}
                  </div>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => void removeMaintenanceWindow(maintenanceWindow)}
                    disabled={deletingWindowId === maintenanceWindow.id}
                    aria-label={`Delete maintenance window ${maintenanceWindow.id}`}
                  >
                    {deletingWindowId === maintenanceWindow.id ? 'Deleting…' : 'Delete'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {affectedBookings !== null && <AffectedBookingsPanel bookings={affectedBookings} />}
    </div>
  );
}

const panelStyle: CSSProperties = {
  background: PS.white,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 4,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const dayRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  borderBottom: `1px solid ${PS.borderLight}`,
  paddingBottom: 10,
};

const maintenanceFormStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'end',
  flexWrap: 'wrap',
};

const fieldStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
};

const inputStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  boxSizing: 'border-box',
  background: PS.white,
  color: PS.nearBlack,
  ...T.ui,
};

const windowsListStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const windowRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 0',
  borderBottom: `1px solid ${PS.borderLight}`,
};

const closedLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  minHeight: 40,
  ...T.ui,
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  ...T.ui,
};

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: `1px solid ${PS.border}`,
  ...T.label,
};

const tdStyle: CSSProperties = {
  padding: '10px',
  borderBottom: `1px solid ${PS.borderLight}`,
  verticalAlign: 'top',
};

const errorStyle: CSSProperties = {
  padding: 12,
  background: PS.errorBg,
  border: `1px solid ${PS.errorBorder}`,
  borderRadius: 3,
  color: PS.errorText,
  ...T.ui,
};

const retryStyle: CSSProperties = {
  marginLeft: 8,
  border: 0,
  background: 'transparent',
  color: PS.linkBlue,
  cursor: 'pointer',
  ...T.ui,
};
